<template>
  <main class="ref-page watch-page">
    <PageHeader :title="videoInfo.vod_name || $t('frontend.player.title')" back />
    <van-dialog v-model="noticeVisible" class="watch-notice" :title="noticeTitle" :message="noticeMessage" :allow-html="false" :confirm-button-text="$t('frontend.player.understood')" />
    <section class="watch-content">
      <LoadState v-if="loading" loading />
      <LoadState v-else-if="error && !videoInfo.id" :error="error" @retry="loadVideo" />
      <div v-show="authorized" class="watch-player"><video ref="video" class="video-js vjs-big-play-centered" playsinline></video></div>
      <button v-if="!loading && videoInfo.id && !authorized" class="watch-preview" :disabled="starting" :aria-label="starting ? $t('frontend.player.opening') : $t('frontend.player.play_cost', { cost: watchCost })" @click="startWatch">
        <img v-if="cover" class="watch-cover" :src="cover" alt="">
        <span class="watch-play-icon" aria-hidden="true"><van-loading v-if="starting" color="#fff" /><van-icon v-else name="play" /></span>
      </button>
      <div v-if="!loading && videoInfo.id" class="watch-details">
        <h2>{{ videoInfo.vod_name }}</h2>
        <span v-if="videoInfo.count !== undefined && videoInfo.count !== null" class="watch-count">{{ $t('frontend.player.play_count', { count: playCount }) }}</span>
        <p class="watch-balance">{{ $t('frontend.player.ticket_balance') }}<strong>{{ balance }}</strong></p>
        <template v-if="!authorized">
          <p class="watch-note">{{ $t('frontend.player.watch_cost', { cost: watchCost }) }}</p>
          <p class="watch-note">{{ $t('frontend.player.resume_note') }}</p>
          <p class="watch-note">{{ $t('frontend.player.ticket_note') }}</p>
          <p v-if="error" role="alert" class="watch-error">{{ error }}</p>
        </template>
        <p v-else class="watch-note">{{ $t('frontend.player.watch_saved', { cost: watchCost }) }}</p>
        <div v-if="playbackError" class="watch-error" role="alert"><p>{{ playbackError }}</p><button class="ref-text-button" @click="retryPlayback">{{ $t('frontend.player.retry_playback') }}</button></div>
        <router-link class="ref-text-button" to="/WatchHistory">{{ $t('frontend.player.view_history') }}</router-link>
      </div>
    </section>
    <section v-if="moreVideoInfo.length" class="ref-section">
      <h2 class="watch-heading">{{ $t('frontend.player.recommended') }}</h2>
      <div class="ref-video-grid"><VideoCard v-for="item in moreVideoInfo" :key="item.id" :item="item" /></div>
    </section>
  </main>
</template>

<script>
import videojs from "../../common/video-player";
import "videojs-contrib-hls";
import playerZh from "video.js/dist/lang/zh-TW.json";
import playerJa from "video.js/dist/lang/ja.json";
import playerKo from "video.js/dist/lang/ko.json";
import PageHeader from "../../common/PageHeader";
import LoadState from "../../common/LoadState";
import VideoCard from "../../common/VideoCard";
import { assertResponse, displayNumber, imageUrl, rowsOf } from "../../common/reference";
import { clearSession } from "../../common/language";

videojs.addLanguage("zh-tw", playerZh);
videojs.addLanguage("ja", playerJa);
videojs.addLanguage("ko", playerKo);

function newRequestId() {
  if (window.crypto && window.crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
  }
  return `watch_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
}

function validWatchCost(value) {
  if ((typeof value !== "number" && typeof value !== "string") || !/^[1-9]\d*$/.test(String(value))) return null;
  const cost = Number(value);
  return Number.isInteger(cost) && cost <= 2147483647 ? cost : null;
}

export default {
  components: { PageHeader, LoadState, VideoCard },
  data: () => ({
    loading: true, starting: false, authorized: false, error: "", playbackError: "",
    videoInfo: {}, userInfo: {}, moreVideoInfo: [], player: null,
    requestId: "", requestVersion: 0,
    noticeVisible: false, noticeTitle: "", noticeMessage: ""
  }),
  computed: {
    cover() { return imageUrl(this.videoInfo.vod_pic); },
    balance() { return displayNumber(this.userInfo.video_score); },
    playCount() { return displayNumber(this.videoInfo.count); },
    watchCost() { return validWatchCost(this.videoInfo.watch_cost); },
    playerLanguage() { return { zh: "zh-tw", en: "en", ja: "ja", kr: "ko" }[this.$i18n.locale] || "zh-tw"; }
  },
  methods: {
    checkSession(response) {
      if (Number(response && response.code) === 401) {
        clearSession();
        this.$store.commit("setUserInfoValue", {});
        this.$router.push("/Login");
        return false;
      }
      return true;
    },
    async loadVideo() {
      const version = ++this.requestVersion;
      this.loading = true;
      this.starting = false;
      this.authorized = false;
      this.error = "";
      this.noticeVisible = false;
      this.noticeMessage = "";
      this.playbackError = "";
      this.videoInfo = {};
      this.moreVideoInfo = [];
      this.requestId = newRequestId();
      if (this.player) this.player.reset();
      if (!localStorage.getItem("token")) { this.loading = false; this.$router.push("/Login"); return; }
      const id = String(this.$route.query.id || "");
      if (!/^[1-9]\d*$/.test(id)) { this.loading = false; this.error = this.$t('frontend.player.not_found'); return; }
      try {
        const [video, user] = await Promise.all([
          this.$http({ method: "get", url: "video_get_info", data: { id } }),
          this.$http({ method: "get", url: "user_info" })
        ]);
        if (version !== this.requestVersion) return;
        if (!this.checkSession(user) || !this.checkSession(video)) return;
        const info = assertResponse(video);
        const account = assertResponse(user);
        if (Number(account.status) !== 1) {
          this.checkSession({ code: 401 });
          return;
        }
        if (!info.id) throw new Error(this.$t('frontend.player.not_found'));
        if (!validWatchCost(info.watch_cost)) throw new Error(this.$t('frontend.player.cost_unavailable'));
        this.videoInfo = info;
        this.userInfo = account;
        this.$store.commit("setUserInfoValue", account);
        this.loadMoreVideos(version);
      } catch (error) {
        if (version === this.requestVersion) this.error = error.message || this.$t('frontend.player.load_failed');
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    },
    async loadMoreVideos(version) {
      try {
        const result = assertResponse(await this.$http({ method: "get", url: "video_get_more_item" }));
        if (version === this.requestVersion) this.moreVideoInfo = rowsOf(result).filter(item => String(item.id) !== String(this.$route.query.id));
      } catch (error) { /* Recommendations must not interrupt a paid viewing session. */ }
    },
    async startWatch() {
      if (this.loading || this.starting || this.authorized || !this.videoInfo.id || !this.watchCost) return;
      if (!localStorage.getItem("token")) { this.$router.push("/Login"); return; }
      const version = this.requestVersion;
      let noticeTitle = this.$t('frontend.player.cannot_start');
      this.starting = true;
      this.error = "";
      this.noticeVisible = false;
      try {
        const response = await this.$http({
          method: "post", url: "video_start_watch",
          data: { id: this.$route.query.id, request_id: this.requestId, expected_cost: this.watchCost }
        });
        if (version !== this.requestVersion) return;
        if (!this.checkSession(response)) return;
        if ([402, 409].includes(Number(response && response.code))) {
          const details = response.data || {};
          const currentCost = validWatchCost(details.watch_cost !== undefined ? details.watch_cost : details.required);
          if (currentCost) this.videoInfo = { ...this.videoInfo, watch_cost: currentCost };
          const balance = response.data && response.data.balance;
          if ((typeof balance === "number" || typeof balance === "string") && /^\d+(?:\.\d+)?$/.test(String(balance)) && Number.isFinite(Number(balance))) {
            this.userInfo = { ...this.userInfo, video_score: balance };
            this.$store.commit("setUserInfoValue", this.userInfo);
          }
          if (Number(response.code) === 402) {
            noticeTitle = this.$t('frontend.player.insufficient_tickets');
            const message = typeof details.insufficient_message === "string" && details.insufficient_message.trim()
              ? details.insufficient_message
              : this.$t('frontend.player.insufficient_message', { cost: this.watchCost, balance: this.balance });
            throw new Error(message);
          }
          if (currentCost) {
            noticeTitle = this.$t('frontend.player.cost_changed');
            throw new Error(this.$t('frontend.player.cost_changed_message', { cost: currentCost }));
          }
        }
        const info = assertResponse(response);
        if (!info.vod_play_url || info.balance === undefined || !validWatchCost(info.watch_cost)) throw new Error(this.$t('frontend.player.incomplete_data'));
        this.videoInfo = info;
        this.userInfo = { ...this.userInfo, video_score: info.balance };
        this.$store.commit("setUserInfoValue", this.userInfo);
        this.authorized = true;
        await this.$nextTick();
        if (version === this.requestVersion) this.retryPlayback();
      } catch (error) {
        if (version === this.requestVersion) {
          this.error = error.message || this.$t('frontend.player.start_failed');
          this.noticeTitle = noticeTitle;
          this.noticeMessage = this.error;
          this.noticeVisible = true;
        }
      } finally {
        if (version === this.requestVersion) this.starting = false;
      }
    },
    retryPlayback() {
      if (!this.authorized || !this.player) return;
      const version = this.requestVersion;
      this.playbackError = "";
      try {
        const url = this.videoInfo.vod_play_url;
        const type = /\.mp4(?:[?#]|$)/i.test(url) ? "video/mp4" : "application/x-mpegURL";
        this.player.src([{ src: url, type }]);
        this.player.poster(this.cover);
        const playing = this.player.play();
        if (playing && playing.catch) playing.catch(() => {
          if (version === this.requestVersion) this.playbackError = this.$t('frontend.player.not_started');
        });
      } catch (error) {
        if (version === this.requestVersion) this.playbackError = this.$t('frontend.player.play_failed');
      }
    }
  },
  watch: {
    "$route.query.id"() { this.loadVideo(); },
    playerLanguage(language) { if (this.player) this.player.language(language); }
  },
  mounted() {
    this.player = videojs(this.$refs.video, { controls: true, preload: "none", fluid: true, playsinline: true, language: this.playerLanguage });
    this.player.on("error", () => {
      if (this.authorized) this.playbackError = this.$t('frontend.player.play_failed');
    });
    this.loadVideo();
  },
  beforeDestroy() {
    this.requestVersion++;
    this.noticeVisible = false;
    if (this.player) { this.player.dispose(); this.player = null; }
  }
};
</script>

<style scoped>
.watch-content { padding: 0; }
.watch-player { width: 100%; background: #000; }
.watch-preview { position: relative; display: block; width: 100%; aspect-ratio: 16 / 9; padding: 0; border: 0; color: #fff; background: #000; overflow: hidden; }
.watch-preview:disabled { opacity: 1; }
.watch-cover { position: absolute; inset: 0; display: block; width: 100%; height: 100%; object-fit: contain; }
.watch-play-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center; width: 3.2rem; height: 2.2rem; border: 1px solid #fff; border-radius: .35rem; font-size: 1.65rem; background: #20272ec9; }
.watch-details { padding: .5rem .5rem .8rem; border-bottom: .3rem solid #f2f2f5; }
.watch-details h2 { margin: 0 0 .25rem; color: #43248d; font-size: 1.134rem; line-height: 1.4; overflow-wrap: anywhere; }
.watch-count { display: inline-block; padding: .2rem .35rem; border-radius: .6rem; color: #bc32f2; background: #f2f2f5; font-size: 0.81rem; }
.watch-details p { margin: .4rem 0; line-height: 1.6; }
.watch-balance { font-size: 0.864rem; }
.watch-heading { font-size: 1.08rem; line-height: 1.5; }
.watch-note { color: #84778d; font-size: 0.81rem; }
.watch-error { color: #b93551; font-size: 0.864rem; overflow-wrap: anywhere; }
.watch-details > a { display: inline-block; padding: .35rem 0; }
.watch-notice { width: calc(100% - 2rem); max-width: 24rem; border-radius: .7rem; }
.watch-notice ::v-deep .van-dialog__header { padding-top: 1.2rem; font-size: 1.134rem; line-height: 1.5; }
.watch-notice ::v-deep .van-dialog__message { padding: 1.2rem; font-size: 0.972rem; line-height: 1.7; overflow-wrap: anywhere; }
.watch-notice ::v-deep .van-dialog__confirm { height: 3rem; font-size: 1.026rem; }
</style>
